import { Dayjs } from '@seolens/date-manager';
import { Col, Empty, Flex, Form, Row } from 'antd';
import { FC, memo, useState } from 'react';

import { AppMessage, CircleSpinnerIcon } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, getTodayInTimeZone, messageOnError } from '@/common/utils';

import {
    DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD,
    SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE,
    SEARCH_CONSOLE_TIME_ZONE,
    SearchConsoleAnalyticsFormFields,
    SearchConsoleAnalyticsFormInputs,
    SearchConsoleAnalyticsFormView,
    SearchConsoleAnalyticsRequestData,
    SearchConsoleAnalyticsResultView,
    searchConsoleAnalyticsFormValidationSchema
} from '@/modules/Domains';
import { SiteGroupSearchAnalytics, useGetSiteGroupsSearchAnalyticsMutation } from '@/modules/SiteGroups';

import { SiteGroupsSearchAnalyticsDomainsView } from './SiteGroupsSearchAnalyticsDomainsView';

interface Props {
  siteGroupId: string;
}

export const SiteGroupsSearchAnalyticsContainer: FC<Props> = memo(({ siteGroupId }) => {
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<SearchConsoleAnalyticsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SearchConsoleAnalyticsFormInputs>>(null);
  const [analyticsResult, setAnalyticsResult] = useState<SiteGroupSearchAnalytics | null>(null);

  const {
    mutate: getAnalyticsMutation,
    isLoading: isGetAnalyticsMutationLoading
  } = useGetSiteGroupsSearchAnalyticsMutation(siteGroupId)({
    onMutate: () => {
      setAnalyticsResult(null);
    },
    onSuccess: (data) => {
      appMessageApi.success('Запит виконано успішно!');

      setAnalyticsResult(data);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SearchConsoleAnalyticsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const today = getTodayInTimeZone(SEARCH_CONSOLE_TIME_ZONE);

  const initialValues: SearchConsoleAnalyticsFormFields = {
    dataState: 'final',
    startDate: SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE[DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD](today),
    endDate: today
  };

  const handleFinish = (values: SearchConsoleAnalyticsFormFields): void => {
    setFormErrors(null);
    getAnalyticsMutation({
      dataState: values.dataState as SearchConsoleAnalyticsRequestData['dataState'],
      startDate: (values.startDate as Dayjs).format('YYYY-MM-DD'),
      endDate: (values.endDate as Dayjs).format('YYYY-MM-DD')
    });
  };

  const handleFinishFailed = (): void => {
    setFormErrors(null);
  };

  const handleToggleDataState = (checked: boolean): void => {
    form.setFieldValue([SearchConsoleAnalyticsFormInputs.DataState], checked ? 'all' : 'final');
  };

  return (
    <Row gutter={ [24, 16] }>
      <Col xs={ 24 } lg={ 12 } xl={ 10 }>
        <SearchConsoleAnalyticsFormView
          formName="site-group-search-analytics-form"
          formType="create"
          form={ form }
          formErrors={ formErrors }
          initialValues={ initialValues }
          isFormLoading={ isGetAnalyticsMutationLoading }
          validationSchema={ searchConsoleAnalyticsFormValidationSchema }
          handleBack={ () => {} }
          handleToggleDataState={ handleToggleDataState }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      </Col>

      <Col xs={ 24 } lg={ 12 } xl={ 14 }>
        { isGetAnalyticsMutationLoading && (
          <Flex align="center" justify="center" style={{flexDirection: 'column', minHeight: 150 }}>
            <CircleSpinnerIcon size={24}/>
            <div>Збираємо дані з Search Console...</div>
          </Flex>
        ) }

        { !isGetAnalyticsMutationLoading && !analyticsResult && (
          <Flex align="center" justify="center" style={{ minHeight: 150 }}>
            <Empty description="Оберіть період і виконайте запит" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
          </Flex>
        ) }

        { analyticsResult && (
          <SiteGroupsSearchAnalyticsDomainsView
            responseAggregationType={ analyticsResult.responseAggregationType }
            domains={ analyticsResult.domains }
          />
        ) }
      </Col>

      { analyticsResult && (
        <Col span={ 24 }>
          <SearchConsoleAnalyticsResultView result={ analyticsResult }/>
        </Col>
      ) }
    </Row>
  );
});
